/**
 * Helper cliente: aplana el árbol de carpetas de un cliente.
 *
 * Schema real DAYSI:
 *   folders/{customerId}   ← doc raíz con folders[]
 *     folders: [{ name, description, isUpload, reference: { path, id } }]
 *   y cada reference apunta a otro doc con su propio folders[].
 *
 * Devuelve un array plano [{ name, displayPath, path, description, isUpload }]
 * útil para mostrar en un Select de "Mover a..." o como contexto para la IA.
 */

const MAX_DEPTH = 6;
const MAX_OUT = 200;

/**
 * Modelo real DAYSI: cada entrada de `folders[]` declara una SUB-COLECCIÓN
 * cuyo nombre es `reference.id`, colgada del documento actual. Navegar a un
 * folder = `docActual.collection(reference.id)`; los DOCUMENTOS dentro tienen
 * sus propios `folders[]`. Espeja loadAllFoldersRecursively.js / upload.js.
 * `reference.path` (DocumentReference al padre) NO se usa para navegar.
 */
export const listAllFoldersClient = async (firestore, customerId) => {
  if (!customerId) return [];
  const out = [];
  const rootRef = firestore.collection('folders').doc(customerId);
  const queue = [{ docRef: rootRef, trail: [] }];

  while (queue.length > 0 && out.length < MAX_OUT) {
    const { docRef, trail } = queue.shift();
    if (trail.length > MAX_DEPTH) continue;

    let snap;
    try { snap = await docRef.get(); } catch { continue; }
    if (!snap.exists) continue;

    const list = Array.isArray(snap.data()?.folders) ? snap.data().folders : [];
    for (const entry of list) {
      if (!entry?.name || !entry?.reference?.id) continue;

      // reference.id = nombre de la subcolección del folder.
      const colRef = docRef.collection(entry.reference.id);
      const displayPath = [...trail, entry.name].join(' / ');
      out.push({
        id: colRef.path,
        name: entry.name,
        displayPath,
        path: colRef.path,
        description: entry.description || '',
        isUpload: entry.isUpload !== false,
      });

      // Sub-sub-carpetas: iterar los documentos de la subcolección.
      let colSnap;
      try { colSnap = await colRef.get(); } catch { colSnap = null; }
      if (colSnap && !colSnap.empty) {
        for (const childDoc of colSnap.docs) {
          queue.push({ docRef: childDoc.ref, trail: [...trail, entry.name] });
        }
      }
    }
  }
  return out;
};

export default listAllFoldersClient;
